import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Form, Formik } from 'formik'
import { toast } from 'react-toastify'
import { v4 as uuid } from 'uuid'
import { addMinutes, differenceInMilliseconds, format } from 'date-fns'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import Spinner from '../../components/Spinner'
import { api } from '../../services/api'
import Card from '../../components/Card'
import { Button, Textbox } from '../../components/Form'
import { FiAlertTriangle, FiX, FiFileText, FiCheck, FiUser, FiCheckCircle, FiMapPin, FiXCircle } from 'react-icons/fi'
import { validarCPF } from '../../util/validadores'
import cpfApi from '../../services/cpf'
import masks, { isMobile } from '../../util/masks'
import { unmask } from '../../util/string'
import { getAge } from '../../util/date'
import Table from '../../components/Table'
import FileStack from '../../components/FileStack'
import { useInnerWidth } from '../../util/viewport'
import { useHistory } from 'react-router-dom'

let uuidAcesso
let removeAccessTimeout
const accessTimoutMinutes = 30

export default function () {
	const { token } = useParams()

	const { width } = useInnerWidth()

	const history = useHistory()

	const [showFormularioCPF, setshowFormularioCPF] = useState(false)
	const [mostrarCondicoesMedicas, setMostrarCondicoesMedicas] = useState(false)
	const [position, setPosition] = useState(null)
	const [isSendingMessage, setIsSendingMessage] = useState(false)
	const [isTagValid, setIsTagValid] = useState(null)
	const [exameVisualizacao, setExameVisualizacao] = useState(null)
	const [messageWasSent, setMessageWasSent] = useState(false)
	const [contatosNotificados, setContatosNotificados] = useState([])

	const { data } = useQuery({
		queryKey: ['get-dados-emergencia-from-token'],
		queryFn: async () => {
			try {
				if (!uuidAcesso) {
					uuidAcesso = uuid()
				}

				const response = await api.get(`prontuario/emergencia/dados/${token}`)

				setIsTagValid(!!response)

				await api.post('prontuario_acesso', {
					prontuario_id: response.prontuario.id,
					lat: position?.latitude,
					lng: position?.longitude,
					acesso_id: uuidAcesso
				})

				return response || {}
			} catch (e) {
				return {}
			}
		},
		enabled: position !== null
	})

	function getLocation() {
		if ('geolocation' in navigator) {
			navigator.geolocation.getCurrentPosition(
				function (position) {
					setPosition(position.coords)
				},
				function () {
					setPosition(false)
				}
			)
		} else {
			console.error("Geolocalização não é suportada pelo navegador.")
		}
	}

	async function handleVerificarCPF({ cpf }) {
		if (!validarCPF(cpf)) {
			toast.warn('O CPF não é válido. Verifique o número digitado.')

			return
		}

		try {
			const { data: response } = await cpfApi.get(unmask(cpf))

			const nome = response.nome

			if (nome) {
				await api.put(`prontuario_acesso/${uuidAcesso}`, {
					cpf,
					nome,
					lat: position?.latitude,
					lng: position?.longitude
				})

				setMostrarCondicoesMedicas(true)

				setshowFormularioCPF(false)
			} else {
				toast.warn('O CPF não está vinculado a nenhuma pessoa. Verifique o número digitado.')
			}

		} catch (e) {
			toast.error(e.msg)
		}
	}

	async function sendMessage() {
		try {
			const body = {}

			if (position) {
				const localizacaoPermitida = !!position.latitude && !!position.longitude

				if (localizacaoPermitida) {
					body.latitude = position.latitude
					body.longitude = position.longitude
				}
			}

			setIsSendingMessage(true)

			const relatorio = await api.post(`prontuario/emergencia/enviar_mensagem/${token}`, body)

			setIsSendingMessage(false)
			setMessageWasSent(true)
			setContatosNotificados([
				...relatorio.sucesso.map(item => ({ ...item, status: 'sucesso' })),
				...relatorio.falha.map(item => ({ ...item, status: 'falha' }))
			])

			toast.success('Mensagem enviada com sucesso!')
		} catch (e) {
			toast.error(e.msg)
		}
	}

	async function storeAccessControl(data) {
		const storageData = localStorage.getItem('zelai:prontuario-acesso')
		const dataServer = await api.get('now')

		if(!storageData) {
			data.expiration = format(addMinutes(new Date(dataServer.now), accessTimoutMinutes), 'yyyy-MM-dd HH:mm:ss')
			localStorage.setItem('zelai:prontuario-acesso', JSON.stringify(data))
			return accessTimoutMinutes * 60 * 1000
		}
		
		const { expiration } = JSON.parse(storageData)
		const expirationDatetime = new Date(expiration)
		const remainingTime = differenceInMilliseconds(expirationDatetime, new Date(dataServer.now))

		if(remainingTime > 0) {
			return differenceInMilliseconds(expirationDatetime, new Date(dataServer.now))
		} else { // nova emergência, tudo começa do zero
			localStorage.removeItem('zelai:prontuario-acesso')
			
			const newExpiration = addMinutes(new Date(dataServer.now), accessTimoutMinutes)
			data.expiration = format(newExpiration, 'yyyy-MM-dd HH:mm:ss')
			
			localStorage.setItem('zelai:prontuario-acesso', JSON.stringify(data))

			return differenceInMilliseconds(newExpiration, new Date(dataServer.now))
		}
	}

	async function defineAccessTimeout() {
		const remainingMilliseconds = await storeAccessControl({ token })

		removeAccessTimeout = setTimeout(() => {
			setMostrarCondicoesMedicas(false)

			localStorage.removeItem('zelai:prontuario-acesso')

			history.replace('/login')
		}, remainingMilliseconds)
	}

	useEffect(() => {
		if(mostrarCondicoesMedicas) {
			defineAccessTimeout()

			let timeout = 60
			const interval = setInterval(() => {
				timeout--

				if(timeout === 0) {
					clearInterval(interval)
				}
			}, 1000)

			return () => { 
				clearTimeout(removeAccessTimeout) 
				clearInterval(interval)
			}
		}
	}, [mostrarCondicoesMedicas])

	useEffect(() => {
		getLocation()
	}, [])

	if (isTagValid === false) {
		return (
			<div className="my-8 md:mx-16 lg:mx-24 flex flex-col items-center">
				<Card className="flex flex-col gap-4 bg-white text-slate-800 max-w-96 items-center">
					<FiAlertTriangle size={40} className="text-red-500" />

					<h1 className="font-bold text-center text-slate-800" style={{ fontSize: 16 }}>
						Este cartão de emergência não foi reconhecido.
					</h1>

					<span className="text-slate-500 self-center" style={{ fontSize: 13 }}>Verifique o QRCode e tente novamente.</span>
					<span className="text-slate-500 self-start" style={{ fontSize: 13 }}>Se o problema persistir, talvez este cartão tenha sido cancelado pelo proprietário.</span>
				</Card>
			</div>
		)
	}

	if (!data || isTagValid === null) {
		return (
			<div className="my-[25vh]">
				<Spinner />
			</div>
		)
	}

	const informacoes = data.informacoes.reduce((result, item) => {
		return {
			...result,
			[item.pergunta]: !Number.isNaN(Number(item.resposta)) ? !!Number(item.resposta) : item.resposta
		}
	}, {})

	const dadosClinicos = data.dados_clinicos.reduce((result, item) => {
		return {
			...result,
			[item.tipo]: item.valor
		}
	}, {})

	const exames = data.exames.map(exame => ({
		id: exame.id,
		descricao: exame.descricao,
		medico: `${exame.nome} (${exame.crm})`,
		tipo: exame.tipo,
		data_exame: format(new Date(exame.data_exame), 'dd/MM/yyyy'),
		data_resultado: format(new Date(exame.data_resultado), 'dd/MM/yyyy'),
		arquivos_exame: exame.arquivos_exame
	}))

	return (
		<div className="my-8 md:mx-16 lg:mx-24 flex flex-col items-center">
			<Card className="flex flex-col gap-6 bg-white text-slate-800 w-full">
				<h1 className="font-bold text-center text-slate-800" style={{ fontSize: 16 }}>CARTÃO DE EMERGÊNCIA MÉDICA</h1>

				<h1 className="text-lg text-slate-800" style={{ fontWeight: 700 }}>{data.nome}</h1>

				{data.data_nascimento && <h2 className="text-slate-800 mx-2 sm:mx-0" style={{ fontSize: 15, fontWeight: 500 }}>{getAge(data.data_nascimento)} anos</h2>}

				<div className="flex flex-col gap-4">
					{!!position ? (
						<div className="text-emerald-500 font-semibold grid grid-cols-[1.5rem_auto] gap-1">
							<FiCheck size={20} />
							<span>A localização do ocorrido já foi detectada por nosso sistema.</span>
						</div>
					) : position === false ? (
						<div className="text-red-700 font-semibold grid grid-cols-[1.5rem_auto] gap-1">
							<FiX size={20} />
							<span>Não foi possível acessar sua localização. Podemos enviar a mensagem de emergência mesmo assim, porém recomendamos que, antes, você acesse as configurações do navegador e permita o acesso ao local onde se encontra.</span>
						</div>
					) : (
						<div className="text-slate-700 font-semibold grid grid-cols-[1.5rem_auto] gap-1 animate-pulse" style={{ animationDuration: '500ms' }}>
							<FiMapPin size={20} />
							<span>Estamos obtendo sua localização...</span>
						</div>
					)}

					<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Contatos de emergência</h1>

					{!!data.contatos?.length ? (
						<>
							<Button className="bg-red-500 text-white w-full h-fit self-center" onClick={sendMessage} loading={isSendingMessage} disabled={position === null}>
								Enviar mensagem de emergência
								<FiAlertTriangle size={18} />
							</Button>

							{messageWasSent && (
								<>
									<div className="text-emerald-500 font-semibold grid grid-cols-[1.5rem_auto] gap-1 animated flash faster">
										<FiCheck size={20} />
										<span>A mensagem de emergência foi enviada para os contatos de emergência.</span>
									</div>

									<p>Caso queira, você também pode telefonar para um dos contatos listados abaixo:</p>

									<ul className="flex flex-col">
										{data.contatos.filter(contato => isMobile(contato.valor)).map((contato, index) => {
											const whatsapp = masks.unmask.mobile(contato.valor)
											const itemRelatorio = contatosNotificados.find(ctt => ctt.id === contato.id)
											const foiNotificado = itemRelatorio.status === 'sucesso'

											return (
												<li key={contato.id} className="grid grid-cols-[2rem_auto_6rem_6rem] text-slate-800 items-center border-b border-slate-200 py-4 md:py-2">
													<FiUser size={18} />
													<span>{`Contato ${index + 1}`}</span>

													{foiNotificado ? (
														<div className={`grid grid-cols-[1.4rem_7rem] items-center text-xs text-emerald-600 animated bounceIn delay-${200 * index}ms faster`}>
															<FiCheckCircle size={17} />
															<span>Enviado!</span>
														</div>
													) : (
														<div className={`grid grid-cols-[1.4rem_7rem] items-center text-xs text-red-600 animated bounceIn delay-${200 * index}ms faster`}>
															<FiXCircle size={17} />
															<span>Não enviado!</span>
														</div>
													)}

													<a href={`tel:+55${whatsapp}`} target="_blank" className="button tiny">
														Telefonar
													</a>
												</li>
											)
										})}
									</ul>
								</>
							)}
						</>
					) : (
						<div>Esta pessoa não possui nenhum contato de emergência cadastrado.</div>
					)}
				</div>

				<div className="flex flex-col gap-2">
					<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Telefones úteis</h1>
					<ul className="mx-2 text-slate-800">
						<li><span className="font-semibold">192</span> - Atendimento Médico de Urgência (SAMU)</li>
						<li><span className="font-semibold">193</span> - Corpo de Bombeiros</li>
					</ul>
				</div>

				{!showFormularioCPF && !mostrarCondicoesMedicas && (
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-3 ml-2 bg-slate-200 p-2 rounded-md">
							<p>Os dados exibidos nesta tela são dados pessoais e sensíveis pertencentes a <span className="font-bold">{data.nome}</span>.</p>
							<p>O acesso a estas informações só pode ser feito mediante autorização desta pessoa, conforme previsto na <span className="font-bold">Lei Geral de Proteção de Dados (Lei nº 13.709/2018 – LGPD)</span>.</p>
							<p>É proibido compartilhar, reproduzir ou utilizar esses dados para qualquer finalidade sem o consentimento do titular. O uso indevido pode acarretar responsabilidades legais.</p>
						</div>

						{width < 640 ? (
							<Button onClick={() => { setshowFormularioCPF(true) }} className="w-full sm:w-fit sm:self-center button sm:hidden" style={{ height: 64, fontSize: 15 }}>
								Acessar dados médicos de {data.nome}
							</Button>
						) : (
							<Button onClick={() => { setshowFormularioCPF(true) }} className="w-full sm:w-fit sm:self-center button hidden sm:block">
								Acessar dados médicos de {data.nome}
							</Button>
						)}
					</div>
				)}

				{showFormularioCPF && (
					<Formik
						onSubmit={handleVerificarCPF}
						initialValues={{
							cpf: ''
						}}
					>
						{({ isSubmitting }) => (
							<Form className="flex flex-col gap-3">
								<p className="mx-2">Para ter acesso aos dados médicos de <span className="font-bold">{data.nome}</span>, informe seu CPF para verificação.</p>

								<Textbox
									name="cpf"
									label="Seu CPF"
									mask={masks.cpf}
									disableSubmitWithEnter={false}
								/>

								<Button type="submit" className="button" loading={isSubmitting}>
									Verificar CPF
								</Button>
							</Form>
						)}
					</Formik>
				)}

				{mostrarCondicoesMedicas && (
					<>
						<div className="flex flex-col gap-2">
							<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Condições médicas</h1>
							<ul className="flex flex-col gap-1 mx-2">
								<li><span className="font-semibold">Pressão alta</span>: {informacoes.pressao_alta ? 'sim' : 'não'}</li>
								<li><span className="font-semibold">Cardíaco</span>: {informacoes.cardiaco ? 'sim' : 'não'}</li>
								<li><span className="font-semibold">Diabético</span>: {informacoes.diabetes ? 'sim' : 'não'}</li>
								<li><span className="font-semibold">Usa marcapasso</span>: {informacoes.marcapasso ? 'sim' : 'não'}</li>
								<li><span className="font-semibold">Usa prótese metálica</span>: {informacoes.protese_metalica ? 'sim' : 'não'}</li>
								<li><span className="font-semibold">Alergias</span>: {informacoes.alergias || 'nenhuma'}</li>
							</ul>
						</div>

						<div className="flex flex-col gap-2">
							<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Dados clínicos</h1>
							<ul className="flex flex-col gap-1 mx-2">
								<li><span className="font-semibold">Tipo sanguíneo</span>: {dadosClinicos.tipo_sangue}</li>
							</ul>
						</div>

						<div className="flex flex-col gap-2">
							<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Doenças preexistentes</h1>

							{data.doencas.length > 0 ? (
								<ul className="flex flex-col gap-1 mx-2">
									{data.doencas.map(doenca => (
										<li key={doenca.id}>
											{doenca.doenca}
										</li>
									))}
								</ul>
							) : <span className="mx-2 italic text-slate-500 font-thin" style={{ fontSize: 13 }}>Não informado</span>}

						</div>

						<div className="flex flex-col gap-2">
							<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Medicamentos de uso contínuo</h1>

							{data.medicamentos.length > 0 ? (
								<ul className="flex flex-col gap-1 mx-2">
									{data.medicamentos.map(medicamento => (
										<li key={medicamento.id}>
											{medicamento.medicamento}
										</li>

									))}
								</ul>
							) : <span className="mx-2 italic text-slate-500 font-thin" style={{ fontSize: 13 }}>Não informado</span>}
						</div>

						{/*
						<div className="flex flex-col gap-2">
							<h1 className="text-lg text-slate-800" style={{ fontSize: 18 }}>Exames e laudos</h1>

							{!!exames.length ? (
								<>
									{exameVisualizacao && (
										<div className="flex flex-col gap-3 border rounded-md border-slate-300 py-3 px-4 animated fadeInUp faster bg-white">
											<div className="grid grid-cols-[auto_3rem] gap-2 lg:gap-4">
												<div className="w-full flex flex-col lg:flex-row lg:justify-between gap-4 lg:items-center font-semibold">
													<span>{`Solicitante: ${exameVisualizacao.medico}`}</span>

													<div className="flex flex-col lg:flex-row gap-2 lg:gap-6">
														<span>Data do exame: {exameVisualizacao.data_exame}</span>
														<span>Data do resultado: {exameVisualizacao.data_resultado}</span>
													</div>
												</div>

												<FiX size={40} className="hover:bg-slate-200 text-slate-500 p-2 cursor-pointer rounded-md" onClick={() => { setExameVisualizacao(null) }} />
											</div>

											<div>
												<FileStack
													name="arquivos"
													files={exameVisualizacao.arquivos_exame.map((item, idx) => ({
														...item.arquivo,
														name: `Arquivo ${idx + 1}`
													}))}
												/>
											</div>

											<div className="flex justify-center">
												<Button onClick={() => { setExameVisualizacao(null) }} className="">
													Fechar
												</Button>
											</div>
										</div>
									)}

									<Table
										headers={[
											{ name: 'tipo', value: 'Tipo', centered: true },
											{ name: 'descricao', value: 'Nome do documento' },
											{ name: 'medico', value: 'Médico solicitante' },
											{ name: 'data_exame', value: 'Data de realização', centered: true },
											{ name: 'data_resultado', value: 'Data do resultado', centered: true }
										]}
										data={exames}
										filterable
										hideOnSmallHeaders={[
											'medico',
											'data_exame',
											'data_resultado'
										]}
										hideOffset={0}
										hideHeadersOffset={980}
										style={{ marginTop: 0 }}
										actions={[
											{
												name: 'Visualizar',
												action: setExameVisualizacao,
												icon: FiFileText,
												iconSize: 16
											}
										]}
									/>
								</>
							) : (
								<p className="text-slate-500 italic text-sm mx-2">Nenhum exame cadastrado.</p>
							)}
						</div>
						*/}
					</>
				)}
			</Card>
		</div>
	)
}
